/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import {
  Dropdown,
  MenuList,
  Option,
  SelectMenu,
  Placeholder,
  ValueContainer,
  Control,
  IndicatorsContainer,
  ChevronDown,
} from '../LargeList';

const OPTION_HEIGHT = 44;
const ROWS = 6;

export const filterSubdivision = ({ data }, search) =>
  search === '' || data.name.toLowerCase().includes(search.toLowerCase());

const SelectStateRegionDropdown = ({
  name,
  value,
  placeholder,
  options,
  isLoading,
  onChange,
  handleBlur,
  clearError,
  fieldsInfo,
  containerClass,
  dropDownParentClass,
}) => {
  const { t: translate } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);
  const [displayPlaceholder, setDisplayPlaceholder] = useState(false);
  const selectedName = options.find((option) => option.code === value)?.name || '';

  const toggleFormGroupHeight = (e) => {
    setIsOpened(!isOpened);
    const target = e?.target || document.getElementsByClassName('select_menu-container')[0];
    const parentFormControl = target?.closest('.form_group-container');
    if (!parentFormControl) return;
    parentFormControl.style.height = isOpened
      ? 'auto'
      : `${parentFormControl.offsetHeight + OPTION_HEIGHT * ROWS}px`;
  };

  const onSelectChange = (option) => {
    onChange(option.code, name);
    toggleFormGroupHeight();
  };

  const Input = ({ id, onFocus, onChange: onSearchChange, onBlur }) => (
    <>
      <input
        id={id}
        onFocus={onFocus}
        type="text"
        onChange={onSearchChange}
        onBlur={onBlur}
        className="form-control-search bg-light border-0 ps-4"
        autoComplete="off"
        placeholder={translate('search_state_region')}
        style={{ height: '2.5rem', lineHeight: '2.5rem' }}
      />
      <i
        style={{ left: 5 }}
        className="icon-search icon-search-other text-shadow"
        role="button"
        aria-label="Search"
        tabIndex="-1"
      />
    </>
  );

  Input.propTypes = {
    id: PropTypes.string.isRequired,
    onFocus: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
  };

  const arrowIconClass = isOpened ? 'text-primary' : 'text-shadow';

  return (
    <Dropdown
      isOpen={isOpened}
      onClose={toggleFormGroupHeight}
      dropDownParentClass={dropDownParentClass}
      target={
        <>
          <input
            autoComplete="off"
            type="text"
            className="bg-transparent w-100 form-control-input my-0"
            placeholder={placeholder}
            value={selectedName}
            required
            name={name}
            readOnly
            onFocus={() => setDisplayPlaceholder(true)}
            onBlur={() => setDisplayPlaceholder(false)}
            onClick={toggleFormGroupHeight}
          />
          <i
            tabIndex={0}
            className={`${arrowIconClass} position-absolute top-50 end-0 translate-middle-y`}
            role="button"
            aria-label={placeholder}
            onClick={toggleFormGroupHeight}
            onKeyDown={() => {}}>
            <ChevronDown style={{ transform: `rotate(${isOpened ? '180deg' : '0deg'})` }} />
          </i>
          {displayPlaceholder && <label className="floating-label">{placeholder}</label>}
          <label className="error-label">{translate('input_required_error_label')}</label>
        </>
      }>
      <Select
        autoFocus
        backspaceRemovesValue={false}
        filterOption={filterSubdivision}
        components={{
          IndicatorSeparator: null,
          MenuList,
          Option,
          Menu: SelectMenu,
          Input,
          Placeholder,
          ValueContainer,
          Control,
          IndicatorsContainer,
        }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        isLoading={isLoading}
        menuIsOpen
        onChange={onSelectChange}
        options={options}
        tabSelectsValue={false}
        value={options.find((option) => option.code === value) || null}
        onBlur={(e) => {
          handleBlur(e);
          toggleFormGroupHeight(e);
        }}
        onFocus={clearError}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.code}
        isOptionSelected={(option) => option.code === value}
        fieldsInfo={fieldsInfo}
        containerClass={containerClass}
      />
    </Dropdown>
  );
};

export default SelectStateRegionDropdown;

SelectStateRegionDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  fieldsInfo: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  containerClass: PropTypes.string,
  dropDownParentClass: PropTypes.string,
};

SelectStateRegionDropdown.defaultProps = {
  containerClass: 'w-100 position-absolute bg-lighter rounded shadow-lg p-2 mt-2',
  dropDownParentClass: 'w-100 pe-3',
};
